"use client";

import { useState } from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import {
  type SmartAccountBalances,
  formatUSDBalance,
  formatTokenBalance,
} from "@/hooks/useSmartAccountBalances";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DUST_THRESHOLD = 0.0001;

const PROTOCOL_LOGOS: Record<string, string> = {
  AQUA_LP: "/images/protocols/aqua_round.svg",
  SOROSWAP_LP: "/images/protocols/soroswap_round.svg",
};

// ---------------------------------------------------------------------------
// TokenAvatar
// ---------------------------------------------------------------------------

function TokenAvatar({
  symbol,
  logoUrl,
  size = 32,
}: {
  symbol: string;
  logoUrl?: string | null;
  size?: number;
}) {
  const [imgError, setImgError] = useState(false);
  const showFallback = !logoUrl || imgError;
  const resolvedSrc = logoUrl ?? "";
  const sz = `${String(size)}px`;

  return (
    <div
      className="rounded-full flex-shrink-0 flex items-center justify-center overflow-hidden"
      style={{
        width: sz,
        height: sz,
        background: showFallback ? "var(--accent-dim)" : "var(--surface-hi)",
        border: "1px solid var(--border-subtle)",
        fontSize: size < 24 ? 9 : 11,
      }}
      aria-hidden="true"
    >
      {showFallback ? (
        <span className="font-semibold uppercase" style={{ color: "var(--accent)" }}>
          {symbol.slice(0, 2)}
        </span>
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={resolvedSrc}
          alt={symbol}
          width={size - 6}
          height={size - 6}
          onError={() => { setImgError(true); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AssetRow
// ---------------------------------------------------------------------------

interface AssetRowData {
  symbol: string;
  label: string;
  sublabel: string;
  balance: number;
  price: number | null;
  usdValue: number;
  logoUrl: string | null;
  isDust: boolean;
  isLp: boolean;
}

function AssetRow({ row }: { row: AssetRowData }) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-lg transition-colors",
        "hover:bg-[var(--surface-hi)]",
        row.isDust && "opacity-40",
      )}
    >
      {/* Token identity */}
      <TokenAvatar symbol={row.symbol} logoUrl={row.logoUrl} size={28} />
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-medium leading-tight" style={{ color: "var(--text-primary)" }}>
          {row.label}
        </p>
      </div>

      {/* Balance */}
      <p
        className="text-[11px] font-mono text-right"
        style={{ color: row.isLp ? "var(--text-tertiary)" : "var(--text-secondary)" }}
      >
        {formatTokenBalance(row.balance)}
      </p>

      {/* USD value */}
      <p
        className="text-[12px] font-mono text-right font-medium w-20"
        style={{ color: "var(--text-primary)" }}
      >
        {formatUSDBalance(row.usdValue)}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AssetAllocationCard
// ---------------------------------------------------------------------------

interface AssetAllocationCardProps {
  balances: SmartAccountBalances;
  getLogoBySymbol: (s: string) => string | null;
}

export function AssetAllocationCard({ balances, getLogoBySymbol }: AssetAllocationCardProps) {
  const availableRows: AssetRowData[] = [
    {
      symbol: "USDC",
      label: "USDC",
      sublabel: "Stellar",
      balance: balances.usdc,
      price: 1,
      usdValue: balances.usdc,
      logoUrl: getLogoBySymbol("USDC"),
      isDust: balances.usdc < DUST_THRESHOLD,
      isLp: false,
    },
    {
      symbol: "XLM",
      label: "XLM",
      sublabel: "Stellar",
      balance: balances.xlm,
      price: balances.xlmPrice || null,
      usdValue: balances.xlm * balances.xlmPrice,
      logoUrl: getLogoBySymbol("XLM"),
      isDust: balances.xlm < DUST_THRESHOLD,
      isLp: false,
    },
  ];

  const strategyRows: AssetRowData[] = [
    {
      symbol: "AQUA LP",
      label: "Aqua LP",
      sublabel: "Liquidity Position",
      balance: balances.aquaLp,
      price: null,
      usdValue: balances.aquaLpValue,
      logoUrl: PROTOCOL_LOGOS.AQUA_LP,
      isDust: balances.aquaLp < DUST_THRESHOLD,
      isLp: true,
    },
    {
      symbol: "SOROSWAP LP",
      label: "Soroswap LP",
      sublabel: "Liquidity Position",
      balance: balances.soroswapLp,
      price: null,
      usdValue: balances.soroswapLpValue,
      logoUrl: PROTOCOL_LOGOS.SOROSWAP_LP,
      isDust: balances.soroswapLp < DUST_THRESHOLD,
      isLp: true,
    },
  ];

  const totalAssetCount =
    availableRows.filter((r) => !r.isDust).length +
    strategyRows.filter((r) => !r.isDust).length;

  return (
    <section
      className="rounded-xl overflow-hidden"
      style={{ background: "var(--surface)", border: "1px solid var(--border-default)" }}
      aria-label="Asset allocation"
    >
      {/* Card header */}
      <div
        className="flex items-center justify-between px-5 py-4"
        style={{ borderBottom: "1px solid var(--border-subtle)" }}
      >
        <h2
          className="font-serif font-normal"
          style={{ fontSize: "1.1rem", color: "var(--text-primary)" }}
        >
          Asset Allocation
        </h2>
        <span className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
          {totalAssetCount} asset{totalAssetCount !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Column headers */}
      <div className="flex items-center px-6 pt-3 pb-1">
        <span className="text-[10px] tracking-[0.08em] uppercase font-medium flex-1" style={{ color: "var(--text-tertiary)" }}>Asset</span>
        <span className="text-[10px] tracking-[0.08em] uppercase font-medium text-right" style={{ color: "var(--text-tertiary)" }}>Balance</span>
        <span className="text-[10px] tracking-[0.08em] uppercase font-medium text-right w-20" style={{ color: "var(--text-tertiary)" }}>Value</span>
      </div>

      {/* Available group */}
      <div className="px-2 pt-1 pb-2">
        <p
          className="text-[10px] tracking-[0.08em] uppercase font-medium px-3 py-1"
          style={{ color: "var(--text-tertiary)" }}
        >
          Available
        </p>
        {availableRows.map((row) => (
          <AssetRow key={row.symbol} row={row} />
        ))}
      </div>

      {/* In Strategies group */}
      <div
        className="px-2 pt-2 pb-2"
        style={{ borderTop: "1px solid var(--border-subtle)" }}
      >
        <div className="flex items-center justify-between px-3 py-1">
          <p
            className="text-[10px] tracking-[0.08em] uppercase font-medium"
            style={{ color: "var(--text-tertiary)" }}
          >
            In Strategies
          </p>
          <Link
            href="/strategies"
            className="text-[10px] tracking-[0.04em] transition-opacity hover:opacity-70 rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent)]"
            style={{ color: "var(--accent)" }}
          >
            Browse All Strategies →
          </Link>
        </div>
        {strategyRows.map((row) => (
          <AssetRow key={row.symbol} row={row} />
        ))}
      </div>
    </section>
  );
}
